import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "@usas/ui-web/wrappers/ReactRouterDom";

import {
  AC_STEP_PARENT_WARNING, AC_STEP_USER_INFO
} from "../../utils/AccountCreationConstants";

import useAccountCreationData from "../../../../../../state/landingPages/accountCreation/UseAccountCreationData";

import useExternalAuth from "@usas/ui-web/state/externalAuth/UseExternalAuth";
import { extractTokensFromUrl, isAuthCallback, cleanAuthParamsFromUrl } from "@usas/ui-web/utils/ExternalAuthUtils";
import { useAccountCreationReturnButton } from "../../components/buttons/UseAccountCreationReturnButton";

const INITIAL_LOGIN_ERROR_POPUP_STATE = {
  displayPopup: false,
  errorMessage: ''
};

const useAccountCreationLogin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { updateAccountCreationStep } = useAccountCreationData();
  const { setAccountCreationReturnState } = useAccountCreationReturnButton();
  const { idToken, accessToken, isAuthenticated, setTokens, redirectToExternalAuth } = useExternalAuth();
  const [loginErrorPopupState, setLoginErrorPopupState] = useState(INITIAL_LOGIN_ERROR_POPUP_STATE);

  const onCreateALoginClicked = () => {
    updateAccountCreationStep(AC_STEP_PARENT_WARNING);
  };

  const onCloseLoginErrorPopupClicked = () => {
    setLoginErrorPopupState(INITIAL_LOGIN_ERROR_POPUP_STATE);
  };

  const onExternalLoginClicked = () => {
    redirectToExternalAuth();
  };

  const handleSuccessfulAuthentication = useCallback(() => {
    // Handle successful authentication - redirect to return URL or continue with app flow
    if (searchParams.get('retUrl')) {
      window.location.href = `${searchParams.get('retUrl')}?id_token=${idToken}&access_token=${accessToken}`;
    } else {
      // Clean auth parameters from URL
      const cleanUrl = cleanAuthParamsFromUrl(new URLSearchParams(searchParams));
      window.history.replaceState(null, '', cleanUrl);

      // Continue with app flow or redirect to dashboard
      const currentUrl = window.location.href;
      if (currentUrl.indexOf('?') === -1) {
        window.location.href = `${currentUrl}?id_token=${idToken}&access_token=${accessToken}`;
      } else {
        window.location.href = `${currentUrl}&id_token=${idToken}&access_token=${accessToken}`;
      }
    }
  }, [searchParams, idToken, accessToken]);

  // Handle authentication callback and token extraction
  useEffect(() => {
    // Check if this is an authentication callback
    if (isAuthCallback(searchParams)) {
      const { idToken: urlIdToken, accessToken: urlAccessToken } = extractTokensFromUrl(searchParams);

      if (urlIdToken && urlAccessToken) {
        setTokens(urlIdToken, urlAccessToken);
      } else {
        setLoginErrorPopupState({
          displayPopup: true,
          errorMessage: 'Authentication failed. No valid tokens received from authentication provider.'
        });
      }
    }
  }, [searchParams, setTokens]);

  // Handle successful authentication
  useEffect(() => {
    if (isAuthenticated) {
      handleSuccessfulAuthentication();
    }
  }, [isAuthenticated, handleSuccessfulAuthentication]);

  // skip to registration if parent is creating account for child
  useEffect(() => {
    const memberId = searchParams.get('memberId');

    if (memberId) {
      const childData = {
        memberId,
        firstName: searchParams.get('firstName'),
        lastName: searchParams.get('lastName'),
        middleName: searchParams.get('middleName'),
        preferredName: searchParams.get('preferredName'),
        birthDate: searchParams.get('birthDate')
      };

      updateAccountCreationStep(AC_STEP_USER_INFO, childData, 'childData');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // skip to registration if redirected from another page with returnProject and returnPath params
  useEffect(() => {
    const returnProject = searchParams.get('returnProject');
    const returnPath = searchParams.get('returnPath');

    if (returnProject && returnPath) {
      const returnLabel = searchParams.get('returnLabel') || returnProject;

      setAccountCreationReturnState({
        returnLabel,
        returnProject,
        returnPath
      });

      setSearchParams({}); // Clear all search params, including returnProject and returnPath, to clean up URL

      updateAccountCreationStep(AC_STEP_PARENT_WARNING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isLoading: false, // No longer using accountLoginState
    loginErrorPopupState,
    onExternalLoginClicked,
    onCreateALoginClicked,
    onCloseLoginErrorPopupClicked
  };
};

export default useAccountCreationLogin;